import { addFriend, type SocialError } from "@bn-games/social-client";
import { t } from "@bn-games/i18n-core";
import { el } from "./dom";
import { invalidateFriendIds } from "./friendIds";

// Same reasoning as FriendPickerRow.ts: every user-supplied string below
// reaches the DOM through `el`'s `text` prop (textContent) or an `attrs`
// entry (setAttribute), never `html` — so nothing here needs escaping.

const AVATAR_SIZE_PX = "28";

/**
 * A just-played opponent worth offering to add as a friend. Deliberately not
 * `social-client`'s `Friend` — a `Friend` implies an existing follow, and
 * `avatarUrl` here is nullable because it comes straight off the room
 * roster (`SeatedPlayer.avatarUrl?: string`), which is optional on the wire.
 */
export interface OpponentIdentity {
  readonly userId: string;
  readonly name: string;
  readonly avatarUrl: string | null;
}

/**
 * `/friends/add` (workers/auth/src/handlers/friends.ts) only ever answers 400
 * (missing/self target), 401 (unauthenticated), 404 (target not found), or
 * 429 (rate limited) — it has no `forbidden` (403) path the way
 * `sendInvite`'s `NOT_ACCEPTING_ERROR` does. So there is nothing here to
 * special-case the way `FriendPickerRow.messageFor` special-cases
 * `forbidden`; every kind just surfaces the server's own message.
 */
function messageFor(error: SocialError): string {
  if (error.kind === "rate_limited") {
    return error.retryAfterSeconds !== undefined
      ? t("social.friendPicker.retryIn", { seconds: error.retryAfterSeconds })
      : error.message;
  }
  return error.message;
}

interface AddButtonParts {
  readonly button: HTMLButtonElement;
  readonly errorNode: HTMLElement;
}

/**
 * The add/added/error state machine shared by the single-candidate button
 * and every row inside the multi-candidate modal — one implementation of
 * "click, go busy, land on Added or an inline error," not two. Success/
 * failure are handled locally (mutating this button/error text) so one
 * row's outcome never disturbs another row's in-flight request or "Added"
 * state — same reasoning as `FriendPickerRow`.
 */
function createAddButtonParts(opponent: OpponentIdentity): AddButtonParts {
  const name = opponent.name;
  const errorNode = el("span", { class: "friend-picker__row-error" });
  const button = el(
    "button",
    {
      class: "friend-picker__invite-btn",
      text: t("social.addOpponent.add", { name }),
      attrs: { type: "button", "aria-label": t("social.addOpponent.add", { name }) },
    },
    [],
  );

  let sending = false;

  button.addEventListener("click", () => {
    if (sending || button.disabled) return;
    sending = true;
    button.disabled = true;
    errorNode.textContent = "";

    void addFriend({ userId: opponent.userId }).then((result) => {
      sending = false;
      if (result.ok) {
        invalidateFriendIds();
        button.textContent = t("social.addOpponent.added");
        button.setAttribute("aria-label", t("social.addOpponent.addedAria", { name }));
        button.disabled = true;
        return;
      }
      button.disabled = false;
      errorNode.textContent = messageFor(result.error);
    });
  });

  return { button, errorNode };
}

/** Bare button + inline error, no avatar — the exactly-one-opponent case. */
export function createAddOpponentButton(opponent: OpponentIdentity): HTMLElement {
  const { button, errorNode } = createAddButtonParts(opponent);
  return el("div", { class: "add-opponent__standalone" }, [button, errorNode]);
}

function createAvatar(opponent: OpponentIdentity): HTMLElement {
  return el("img", {
    class: "friend-picker__avatar",
    attrs: {
      ...(opponent.avatarUrl !== null ? { src: opponent.avatarUrl } : {}),
      alt: "",
      width: AVATAR_SIZE_PX,
      height: AVATAR_SIZE_PX,
    },
  });
}

/** One opponent, rendered as a row — used inside the multi-candidate modal. */
export function createAddOpponentRow(opponent: OpponentIdentity): HTMLElement {
  const { button, errorNode } = createAddButtonParts(opponent);
  const info = el("div", { class: "friend-picker__info" }, [
    el("span", { class: "friend-picker__name", text: opponent.name }),
    errorNode,
  ]);
  return el("div", { class: "friend-picker__row" }, [createAvatar(opponent), info, button]);
}
